import "./components";
import "./sbs-timeline.css";

import LiveLike, { LiveLikeWidgets } from "@livelike/engagementsdk";
import { html, LitElement } from "lit";
import { customElement, property, state } from "lit/decorators.js";

import en from "../../../i18n/en/en.json";
import fr from "../../../i18n/fr/fr.json";
import { LocaleKeys, locales, videoManager, shouldFilterWidget } from "./utils";
import { IWidgetPayload } from "@livelike/javascript";

enum WidgetKind {
  ALERT = "alert",
  EMOJI_SLIDER = "emoji-slider",
  VIDEO_ALERT = "video-alert",
  TEXT_PREDICTION = "text-prediction",
  IMAGE_PREDICTION = "image-prediction",
  TEXT_PREDICTION_FOLLOW_UP = "text-prediction-follow-up",
  IMAGE_PREDICTION_FOLLOW_UP = "image-prediction-follow-up",
  CHEER_METER = "cheer-meter",
  SOCIAL_EMBED = "social-embed",
  RICH_POST = "rich-post",
}

const followUpWidgetKinds = [
  WidgetKind.TEXT_PREDICTION_FOLLOW_UP,
  WidgetKind.IMAGE_PREDICTION_FOLLOW_UP,
];

@customElement("sbs-timeline")
export class SBSTimeline extends LitElement {
  @property({ type: String })
  programId: string = "";

  @property({ type: Number })
  widgetCount: number | undefined = undefined;

  @property({ type: String }) language: keyof typeof locales = "en";

  protected createRenderRoot(): HTMLElement | DocumentFragment {
    return this;
  }

  private widgets: HTMLElement | null = null;

  private widgetInteractedCallback?: (arg: any) => void;

  private widgetsInterval: number | null = null;

  private t(key: LocaleKeys): string {
    return locales[this.language][key] || key;
  }

  preventDoubleClick = (event: Event) => {
    event.preventDefault();
  };

  connectedCallback(): void {
    super.connectedCallback();
    this.addPreventDoubleClickListener();
  }

  addPreventDoubleClickListener() {
    document.addEventListener("dblclick", this.preventDoubleClick, {
      passive: false,
    });
  }

  removeListeners() {
    document.removeEventListener("dblclick", this.preventDoubleClick);
    // removing event listener on interaction in livelike-widgets
    if (this.widgets && this.widgetInteractedCallback) {
      this.widgets.removeEventListener(
        "interacted",
        this.widgetInteractedCallback
      );
    }
  }

  disconnectedCallback() {
    super.disconnectedCallback();
    this.removeListeners();
    if (this.widgetsInterval) {
      clearInterval(this.widgetsInterval);
      this.widgetsInterval = null;
    }
    // Clean up video manager when timeline is destroyed
    videoManager.destroy();
  }

  renderEmptyWidgetsScreen() {
    return html`<div class="empty-widgets">
      <span class="title">${this.t("noLiveGames")}</span>
      <span class="description">${this.t("timelineDescription")}</span>
    </div>`;
  }

  renderEmptyWidgets() {
    return html`
      <div class="empty-widgets">
        <span>${this.t("stayTuned")}</span>
      </div>
    `;
  }

  onWidgetCounthandler = (widgetCount: number) => {
    this.widgetCount = widgetCount;
  };

  render() {
    if (!this.programId) {
      return this.renderEmptyWidgetsScreen();
    }
    return html`
      ${this.widgetCount === 0 ? this.renderEmptyWidgets() : null}
      <sbs-internal-timeline
        programId=${this.programId}
        mode="interactive-timeline"
        class="widget-styles"
        ?disabled=${this.widgetCount === 0}
        .handleWidgetCount=${(_widgetCount: number) =>
          this.onWidgetCounthandler(_widgetCount)}
        .lang=${this.language}
      >
        <div slot="widgets-loading" class="sbs-loader-container">
          <sbs-loader
            .customStyle=${{
              height: "60dvh",
            }}
          ></sbs-loader>
        </div>
      </sbs-internal-timeline>
    `;
  }
}

const allowedWidgetKinds = Object.values(WidgetKind);

export class SBSInternalTimeline extends LiveLikeWidgets {
  @property({ type: Number })
  widgetCount: number | undefined = undefined;

  @property({ type: Function })
  handleWidgetCount: Function = () => void 0;

  @state() wordsToReplace: Record<string, string> = {};

  connectedCallback() {
    super.connectedCallback();
    this.setWordsToReplace();
    this.setUpTimeline();
    this.addEventListener("interacted", this.widgetInteractedCallback);
  }

  setWordsToReplace() {
    const enReplaceWords = en.timeline.replaceWords as Record<string, string>;
    const frReplaceWords = fr.timeline.replaceWords as Record<string, string>;

    for (const key in enReplaceWords) {
      if (frReplaceWords[key]) {
        this.wordsToReplace[enReplaceWords[key]] = frReplaceWords[key];
      }
    }
  }

  widgetInteractedCallback = (evt: any) => {
    const { widget, element, item, interacted } = evt.detail;

    if (interacted && widget.kind === WidgetKind.EMOJI_SLIDER) {
      const optionSelected = item.id;
      element.widgetPayload = { ...element.widgetPayload };
      const optionIndex = widget.options.findIndex(
        (option: any) => option.id === optionSelected
      );
      if (optionIndex !== -1) {
        widget.options[optionIndex].vote_count += 1;
      }
      const updatedOptions = [...widget.options];
      element.options = updatedOptions;
      const countvalue = updatedOptions.reduce(
        (a: any, c: any) => a + c.vote_count,
        0
      );
      element.count = countvalue;
    }

    if (interacted && widget.kind === WidgetKind.CHEER_METER) {
      const optionSelected = item.id;
      element.widgetPayload = { ...element.widgetPayload };
      const optionIndex = widget.options.findIndex(
        (option: any) => option.id === optionSelected
      );
      if (optionIndex !== -1) {
        widget.options[optionIndex].vote_count += 1;
      }
      const updatedOptions = [...widget.options];
      element.options = updatedOptions;
      const countvalue = updatedOptions.reduce(
        (a: any, c: any) => a + c.vote_count,
        0
      );
      element.count = countvalue;
    }
  };

  filterAllowedWidgets = (widgets: IWidgetPayload[]) => {
    const followUpWidgets = widgets.filter((w: any) =>
      followUpWidgetKinds.includes(w.kind)
    );
    const nonFollowUpWidgets = widgets.filter(
      (w: any) => !followUpWidgetKinds.includes(w.kind)
    );
    // Replace prediction widgets with their follow-ups when available
    const mergedWidgets = nonFollowUpWidgets.map(
      (w: any) =>
        followUpWidgets.find((fw: any) => fw.id === w.follow_ups?.[0]?.id) ?? w
    );
    return mergedWidgets.filter(
      (widget: any) =>
        allowedWidgetKinds.includes(widget.kind) && !shouldFilterWidget(widget)
    );
  };

  onInitialWidgetsLoaded = ({ widgets }: { widgets: IWidgetPayload[] }) => {
    this.widgetCount = widgets.length;
    this.handleWidgetCount?.(this.widgetCount);
    const filteredAllowedWidgets = this.filterAllowedWidgets(widgets);
    return filteredAllowedWidgets;
  };

  onMoreWidgetsLoaded = ({ widgets }: { widgets: IWidgetPayload[] }) => {
    const filteredAllowedWidgets = this.filterAllowedWidgets(widgets);
    return filteredAllowedWidgets;
  };

  customWidgetRenderer = (args: any) => {
    const widgetPayload = args.widgetPayload;
    switch (widgetPayload.kind) {
      case WidgetKind.ALERT:
        return document.createElement("sbs-text-alert");
      case WidgetKind.EMOJI_SLIDER:
        return document.createElement("sbs-emoji-slider");
      case WidgetKind.VIDEO_ALERT:
        return document.createElement("sbs-video-alert");
      case WidgetKind.TEXT_PREDICTION:
        return document.createElement("sbs-text-prediction");
      case WidgetKind.IMAGE_PREDICTION:
        return document.createElement("sbs-image-prediction");
      case WidgetKind.TEXT_PREDICTION_FOLLOW_UP:
        return document.createElement("sbs-text-prediction-followup-widget");
      case WidgetKind.IMAGE_PREDICTION_FOLLOW_UP:
        return document.createElement("sbs-prediction-followup-widget");
      case WidgetKind.CHEER_METER:
        return document.createElement("sbs-cheer-meter");
      default:
        return null;
    }
  };

  decodeString(encodedStr: string): string {
    const textarea = document.createElement("textarea");
    textarea.innerHTML = encodedStr;
    // returns correctly parsed and decoded string
    return this.replaceEnglishWords(textarea.value);
  }

  replaceEnglishWords(titleText: string) {
    let updatedText = titleText;
    for (const [english, french] of Object.entries(this.wordsToReplace)) {
      // Replace all exact matches by splitting and joining
      while (updatedText.includes(english)) {
        updatedText = updatedText.split(english).join(french);
      }
    }
    return updatedText;
  }

  syncWidgets({
    latestFetchedWidgets,
    displayedWidgets,
    widgets,
  }: {
    latestFetchedWidgets: IWidgetPayload[];
    displayedWidgets: Record<string, string>;
    widgets: any;
  }) {
    const currentWidgetIds = latestFetchedWidgets.map((w) => w.id);
    const displayedWidgetIds = Object.keys(displayedWidgets);

    // Find widgets that are displayed but no longer in the API response
    const widgetsToRemove = displayedWidgetIds.filter(
      (id) => !currentWidgetIds.includes(id)
    );

    // Remove widgets that are no longer in the API response
    widgetsToRemove.forEach((widgetId) => {
      // Find the widget element in the DOM
      const widgetElement = widgets.querySelector(`[widgetid="${widgetId}"]`);

      if (widgetElement) {
        // Remove the widget element from the DOM
        widgetElement.remove();
        // Clean up video manager if it's a video widget
        const widgetPayload = widgetElement.widgetPayload;
        if (widgetPayload?.kind === WidgetKind.VIDEO_ALERT) {
          videoManager.unregisterVideo(widgetId);
        }
      }

      // Remove from displayed widgets tracking
      delete displayedWidgets[widgetId];

      // Update widget count
      if (this.widgetCount && this.widgetCount > 0) {
        this.widgetCount -= 1;
        this.handleWidgetCount?.(this.widgetCount);
      }
    });
  }

  setUpTimeline() {
    setTimeout(() => {
      let latestFetchedWidgets: any[] = [];

      const widgets = document.querySelector("sbs-internal-timeline");

      const allowedWidgetKinds = Object.values(WidgetKind);

      const beforeWidgetAttachedCallback = (event: any) => {
        const { widget } = event.detail;
        const { kind } = widget;

        // Decode question for interactive widgets
        if (kind === WidgetKind.EMOJI_SLIDER) {
          widget.question = this.decodeString(widget.question);
          return;
        }

        // Decode content for alert widgets
        if (kind === WidgetKind.ALERT) {
          widget.text = this.decodeString(widget.text);
        } else if (widget.title) {
          widget.title = this.decodeString(widget.title);
        }
        if (widget.question) {
          widget.question = this.decodeString(widget.question);
        }
      };

      const widgetAttachedCallback = (evt: any) => {
        const { widget, element } = evt.detail;
        const isWidgetAllowed = allowedWidgetKinds.includes(widget.kind);
        if (!isWidgetAllowed || shouldFilterWidget(widget)) return;

        // When a follow-up attaches, remove its parent prediction from the DOM
        // so we never render both at once (regardless of arrival path). Keep
        // the parent's id in displayedWidgets so the next poll doesn't treat
        // it as "new" and re-enqueue it.
        if (followUpWidgetKinds.includes(widget.kind)) {
          const parentPrediction = latestFetchedWidgets.find(
            (w: any) => w.follow_ups?.[0]?.id === widget.id
          );
          if (parentPrediction) {
            const parentEl = widgets.querySelector(
              `[widgetid="${parentPrediction.id}"]`
            );
            if (parentEl) parentEl.remove();
          }
        }
        // Handle video widgets specifically
        if (widget.kind === WidgetKind.VIDEO_ALERT) {
          // Wait for video element to be created and register it with video manager
          setTimeout(() => {
            const videoElement =
              element.querySelector("livelike-video video") ||
              element.querySelector("livelike-widget-body video") ||
              element.querySelector(".sbs-custom-video");

            if (videoElement) {
              const videoId =
                widget.id || `video-${Date.now()}-${Math.random()}`;
              videoManager.registerVideo(videoId, videoElement);
            }
          }, 200);
        }

        const interval = setInterval(() => {
          const updatedWidget = latestFetchedWidgets.find(
            (e) => e.id === widget.id
          );

          if (updatedWidget) {
            element.widgetPayload = updatedWidget;

            if (updatedWidget.options)
              element.options = updatedWidget.options;

            if (updatedWidget.choices) element.choices = updatedWidget.choices;

            if (updatedWidget.average_magnitude)
              element.average_magnitude = updatedWidget.average_magnitude;
          }
        }, 30000);

        const widgetDetachedCallback = () => {
          clearInterval(interval);
          // Clean up video manager for video widgets
          if (widget.kind === WidgetKind.VIDEO_ALERT) {
            const videoId = widget.id || `video-${Date.now()}-${Math.random()}`;
            videoManager.unregisterVideo(videoId);
          }
          element.removeEventListener(widgetAttachedCallback);
          element.removeEventListener(beforeWidgetAttachedCallback);
          element.removeEventListener(
            "beforewidgetdetached",
            widgetDetachedCallback
          );
        };
        element.addEventListener(
          "beforewidgetdetached",
          widgetDetachedCallback
        );
      };

      const displayedWidgets: { [key: string]: string } = {};

      const getWidgets = (callback: (widget: any) => void) =>
        LiveLike.getWidgets({
          programId: (widgets as any).programid,
          ordering: "recent",
          status: "published",
        }).then((resWidgets: any) => {
          // Filter out widgets with timeMin between 50-80 before processing
          const filteredResults = (resWidgets.results || []).filter(
            (widget: any) => !shouldFilterWidget(widget)
          );
          latestFetchedWidgets = filteredResults;
          filteredResults.forEach(callback);
          this.syncWidgets({
            displayedWidgets,
            latestFetchedWidgets,
            widgets,
          });
        });

      const checkOldWidgets = () =>
        getWidgets((widget) => {
          displayedWidgets[widget.id] = widget.id;
        });

      const getNewWidgets = () =>
        getWidgets((widget) => {
          if (!displayedWidgets[widget.id]) {
            (widgets as any).enqueueWidget({
              widgetPayload: widget,
              mode: (widgets as any).mode,
            });
            displayedWidgets[widget.id] = widget.id;
          }
        });

      if (widgets) {
        //@ts-expect-error customWidgetRenderer does not exist
        widgets.customWidgetRenderer = this.customWidgetRenderer;
        widgets.addEventListener(
          "beforewidgetattached",
          beforeWidgetAttachedCallback
        );
        widgets.addEventListener("widgetattached", widgetAttachedCallback);

        checkOldWidgets();
        setInterval(getNewWidgets, 30000);
      }
    }, 0);
  }

  onWidgetReceived = (widgetPayload: any) => {
    // Filter out widgets with timeMin between 50-80
    this.widgetCount = this.widgetCount ? this.widgetCount + 1 : 1;
    this.handleWidgetCount?.(this.widgetCount);
    if (allowedWidgetKinds.includes(widgetPayload.kind)) {
      return widgetPayload;
    }
  };
}

customElements.define("sbs-internal-timeline", SBSInternalTimeline as any);
